/* eslint-disable @typescript-eslint/no-explicit-any */

import { useEffect, useRef } from "react";
import { createStore } from "jotai";

// Create a default store for use outside of React components
let store = {
  ...createStore(),
  check: new Date().getTime(),
};

export const getAtomStore = () => {
  return store;
};

// Listeners for store reset events
const storeResetListeners: Set<() => void> = new Set();

/**
 * Subscribe to store reset events.
 * Used by AtomProvider to re-render when store is reset.
 */
export const subscribeToStoreReset = (callback: () => void) => {
  storeResetListeners.add(callback);
  return () => {
    storeResetListeners.delete(callback);
  };
};

/**
 * Reset the entire Jotai store by creating a new one.
 * This clears ALL atoms at once - use on logout for security.
 * All components using atoms will re-render with initial values.
 */
export const resetAtomStore = () => {
  console.log("[AtomStore] Resetting all atoms (logout)");
  store = {
    ...createStore(),
    check: new Date().getTime(),
  };
  // Notify all listeners (AtomProvider) to re-render with new store
  storeResetListeners.forEach((listener) => listener());
  return store;
};

const PubSub = {
  subscribers: new Map(),

  subscribe: function (event: string, subscriber: (payload: any) => void) {
    if (!this.subscribers.has(event)) {
      this.subscribers.set(event, []);
    }
    this.subscribers.get(event).push(subscriber);
  },

  unsubscribe: function (event: string, subscriber: (payload: any) => void) {
    if (this.subscribers.has(event)) {
      const subscribers = this.subscribers.get(event).filter((sub: (payload: any) => void) => sub !== subscriber);
      this.subscribers.set(event, subscribers);
    }
  },

  publish: function (event: string, payload: any) {
    if (this.subscribers.has(event)) {
      this.subscribers.get(event).forEach((subscriber: (payload: any) => void) => {
        subscriber(payload);
      });
    }
  },
};

export const usePubSub = (event: string, handler: (payload: any) => void) => {
  const handlerRef = useRef<((payload: any) => void) | null>(null);

  useEffect(() => {
    handlerRef.current = handler;
    return () => {
      handlerRef.current = null;
    };
  }, [handler]);

  useEffect(() => {
    const eventHandler = (payload: any) => handlerRef.current?.(payload);
    PubSub.subscribe(event, eventHandler);
    return () => {
      PubSub.unsubscribe(event, eventHandler);
    };
  }, [event]);

  const publish = (payload: any) => {
    PubSub.publish(event, payload);
  };

  return publish;
};
